const actx = typeof window !== 'undefined'
  ? new (window.AudioContext || (window as any).webkitAudioContext)()
  : null

// Global mute state — persisted in localStorage
let _muted = localStorage.getItem('soundMuted') === 'true'

export const SoundManager = {
  isMuted: () => _muted,
  toggle: () => {
    _muted = !_muted
    localStorage.setItem('soundMuted', String(_muted))
    if (_muted) Music.stop()
    return _muted
  },
  setMuted: (v: boolean) => {
    _muted = v
    localStorage.setItem('soundMuted', String(_muted))
    if (_muted) Music.stop()
  },
}

function resume() {
  if (_muted) return
  if (actx && actx.state === 'suspended') actx.resume()
}

function playTone(
  freq: number, type: OscillatorType, duration: number,
  gain: number, freqEnd?: number, delay = 0
) {
  if (!actx || _muted) return
  resume()
  const osc = actx.createOscillator()
  const g   = actx.createGain()
  osc.connect(g); g.connect(actx.destination)
  osc.type = type
  osc.frequency.setValueAtTime(freq, actx.currentTime + delay)
  if (freqEnd) osc.frequency.exponentialRampToValueAtTime(freqEnd, actx.currentTime + delay + duration)
  g.gain.setValueAtTime(0, actx.currentTime + delay)
  g.gain.linearRampToValueAtTime(gain, actx.currentTime + delay + 0.01)
  g.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + delay + duration)
  osc.start(actx.currentTime + delay)
  osc.stop(actx.currentTime + delay + duration + 0.05)
}

function playNoise(duration: number, gain: number, delay = 0, filterFreq = 800) {
  if (!actx || _muted) return
  resume()
  const bufferSize = actx.sampleRate * duration
  const buffer     = actx.createBuffer(1, bufferSize, actx.sampleRate)
  const data       = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1
  const source = actx.createBufferSource()
  const g      = actx.createGain()
  const filter = actx.createBiquadFilter()
  filter.type            = 'bandpass'
  filter.frequency.value = filterFreq
  source.buffer = buffer
  source.connect(filter); filter.connect(g); g.connect(actx.destination)
  g.gain.setValueAtTime(gain, actx.currentTime + delay)
  g.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + delay + duration)
  source.start(actx.currentTime + delay)
  source.stop(actx.currentTime + delay + duration + 0.05)
}

// ── Sound Effects ──────────────────────────────────────────
export const Sounds = {
  go() {
    playTone(400, 'square', 0.06, 0.15, 600)
    playTone(300, 'square', 0.08, 0.10, 200, 0.06)
    playTone(500, 'sine',   0.10, 0.12, 700, 0.12)
  },
  hop() {
    playTone(300, 'sine', 0.08, 0.2, 600)
    playTone(600, 'sine', 0.12, 0.15, 900, 0.07)
    playNoise(0.05, 0.06, 0.08)
  },
  land() {
    playTone(120, 'sine', 0.1, 0.25, 80)
    playNoise(0.06, 0.1)
  },
  cooked() {
    playNoise(0.8, 0.2, 0, 1200)
    playTone(600, 'sawtooth', 0.6, 0.18, 80)
    playTone(400, 'sine',     0.5, 0.12, 60, 0.1)
    playTone(100, 'sine',     0.3, 0.30, 50, 0.5)
  },
  cashout() {
    const notes = [523, 659, 784, 1047]
    notes.forEach((freq, i) => {
      playTone(freq, 'sine', 0.2, 0.2, freq * 1.02, i * 0.1)
    })
    playTone(2000, 'sine', 0.4, 0.08, 1000, 0.35)
    playTone(2500, 'sine', 0.3, 0.06, 1200, 0.40)
  },
tick() {
    playTone(880, 'sine', 0.05, 0.08, 1000)
  },

  // Aviator sounds
  aviatorTakeoff() {
    // Engine revving up
    playTone(80,  'sawtooth', 0.4, 0.15, 200)
    playTone(120, 'sawtooth', 0.5, 0.12, 300, 0.1)
    playTone(200, 'sawtooth', 0.6, 0.10, 450, 0.2)
    playTone(300, 'sine',     0.4, 0.08, 600, 0.35)
    playNoise(0.3, 0.08, 0.1, 2000)
  },

  aviatorTick(multiplier: number) {
    // Rising pitch as multiplier increases
    const freq = Math.min(200 + multiplier * 80, 1200)
    const gain = Math.min(0.04 + multiplier * 0.005, 0.12)
    playTone(freq, 'sine', 0.04, gain, freq * 1.05)
  },

  aviatorCashout() {
    // triumphant win sound
    playTone(523, 'sine', 0.15, 0.20, 540)
    playTone(659, 'sine', 0.15, 0.20, 680, 0.1)
    playTone(784, 'sine', 0.20, 0.22, 800, 0.2)
    playTone(1047,'sine', 0.30, 0.25, 1080, 0.35)
    playTone(2000,'sine', 0.25, 0.08, 1000, 0.5)
  },

  aviatorCrash() {
    // explosion + descending wail
    playNoise(0.6, 0.3, 0, 400)
    playTone(400, 'sawtooth', 0.8, 0.25, 40)
    playTone(200, 'sine',     0.6, 0.15, 30, 0.15)
    playNoise(0.4, 0.15, 0.3, 200)
    playTone(80,  'sine',     0.5, 0.20, 20, 0.4)
  },

  aviatorBetting() {
    // subtle countdown tick
    playTone(440, 'sine', 0.06, 0.06, 460)
  },

// Stepping Stone rescue cashout sounds
  stepRescuePlane() {
    // Propeller buzz
    playTone(140, 'sawtooth', 0.6, 0.07, 170)
    playTone(280, 'sawtooth', 0.6, 0.04, 340, 0.05)
    playNoise(0.3, 0.06, 0.2, 3000)
  },
  stepRescueLift() {
    // Whoosh + rising tone as bird is picked up
    playNoise(0.2, 0.1, 0, 2000)
    playTone(350, 'sine', 0.4, 0.14, 900)
    playTone(500, 'sine', 0.3, 0.10, 1200, 0.1)
  },
  stepRescueFanfare() {
    // Victory fanfare
    const notes = [523, 659, 784, 1047, 1319]
    notes.forEach((f, i) => playTone(f, 'sine', 0.28, 0.2, f*1.01, i * 0.13))
    // Plane flying away (Doppler)
    playTone(420, 'sawtooth', 0.7, 0.09, 40, 0.65)
    playNoise(0.35, 0.05, 0.65, 2500)
  },

  roadStart() {
    playTone(80,  'sawtooth', 0.3, 0.18, 160)
    playTone(100, 'sawtooth', 0.4, 0.15, 250, 0.1)
    playTone(160, 'sawtooth', 0.5, 0.12, 380, 0.2)
    playNoise(0.15, 0.06, 0.3, 1500)
  },
  roadJump() {
    playNoise(0.12, 0.14, 0, 3000)
    playTone(350, 'sine', 0.18, 0.22, 850)
    playTone(900, 'sine', 0.07, 0.12, 950, 0.06)
  },
  roadHorn() {
    playTone(900,  'sine', 0.07, 0.28, 850)
    playTone(1100, 'sine', 0.07, 0.22, 1050, 0.10)
    playTone(900,  'sine', 0.07, 0.28, 850,  0.20)
    playTone(1100, 'sine', 0.07, 0.22, 1050, 0.30)
  },
  roadSafe() {
    playTone(659, 'sine', 0.10, 0.16, 700)
    playTone(784, 'sine', 0.12, 0.16, 820, 0.09)
    playTone(988, 'sine', 0.10, 0.12, 1020, 0.18)
  },
  roadScooterWarn() {
    playTone(1200, 'square', 0.06, 0.18, 1100)
    playTone(1200, 'square', 0.06, 0.18, 1100, 0.13)
    playTone(1400, 'square', 0.06, 0.22, 1300, 0.26)
  },
  roadCrash() {
    playNoise(0.8,  0.25, 0,   800)
    playTone(500, 'sawtooth', 0.6, 0.20, 50)
    playTone(300, 'sine',     0.4, 0.15, 40,  0.10)
    playTone(220, 'sine',     0.3, 0.22, 880, 0.35)
    playTone(880, 'sine',     0.25, 0.18, 60, 0.52)
    playNoise(0.2, 0.08, 0.5, 300)
  },
  roadCashout() {
    const notes = [523, 659, 784, 880, 1047, 1319]
    notes.forEach((f,i) => playTone(f, 'sine', 0.14, 0.20, f*1.01, i*0.09))
    playTone(1047, 'triangle', 0.35, 0.20, 1047, 0.56)
    playTone(1319, 'triangle', 0.30, 0.18, 1319, 0.68)
  },
}

// ── Background Music ───────────────────────────────────────
class BackgroundMusic {
  private running    = false
  private gainNode: GainNode | null = null
  private vol        = 0.18
  private nextBeat   = 0
  private lookahead  = 0.1
  private scheduleId: ReturnType<typeof setTimeout> | null = null
  private beatNum    = 0

  private N: Record<string, number> = {
    C3:130.8, D3:146.8, E3:164.8, F3:174.6, G3:196.0, A3:220.0, B3:246.9,
    C4:261.6, D4:293.7, E4:329.6, F4:349.2, G4:392.0, A4:440.0, B4:493.9,
    C5:523.3, D5:587.3, E5:659.3, G5:784.0, A5:880.0,
  }

  private bpm = 138
  private beatDur() { return 60 / this.bpm }

  private melody = [
    'E4','E4','G4','E4', 'A4','A4','G4','_',
    'E4','G4','A4','C5', 'B4','G4','E4','_',
  ]
  private bass = [
    'C3','_','G3','_', 'A3','_','F3','_',
    'C3','_','G3','_', 'F3','_','G3','_',
  ]
  private chords: string[][] = [
    ['C4','E4','G4'], ['A3','C4','E4'], ['F3','A3','C4'], ['G3','B3','D4'],
  ]

  private note(freq: number, dur: number, gain: number, type: OscillatorType, time: number) {
    if (!actx || !this.gainNode) return
    const osc = actx.createOscillator()
    const g   = actx.createGain()
    osc.connect(g); g.connect(this.gainNode)
    osc.type = type
    osc.frequency.value = freq
    g.gain.setValueAtTime(0, time)
    g.gain.linearRampToValueAtTime(gain, time + 0.01)
    g.gain.exponentialRampToValueAtTime(0.001, time + dur)
    osc.start(time); osc.stop(time + dur + 0.05)
  }

  private noise(dur: number, gain: number, time: number, hipass = false) {
    if (!actx || !this.gainNode) return
    const buf  = actx.createBuffer(1, actx.sampleRate * dur, actx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src    = actx.createBufferSource()
    const g      = actx.createGain()
    const filter = actx.createBiquadFilter()
    filter.type            = hipass ? 'highpass' : 'bandpass'
    filter.frequency.value = hipass ? 6000 : 2000
    src.buffer = buf
    src.connect(filter); filter.connect(g); g.connect(this.gainNode)
    g.gain.setValueAtTime(gain, time)
    g.gain.exponentialRampToValueAtTime(0.001, time + dur)
    src.start(time); src.stop(time + dur + 0.05)
  }

  private playBeat(idx: number, time: number) {
    const bd = this.beatDur()

    // Melody
    const mel = this.melody[idx % 16]
    if (mel !== '_' && this.N[mel]) {
      this.note(this.N[mel], bd * 0.45, 0.45, 'triangle', time)
      this.note(this.N[mel] * 2, bd * 0.3, 0.12, 'sine', time)
    }

    // Bass
    const bas = this.bass[idx % 16]
    if (bas !== '_' && this.N[bas]) {
      this.note(this.N[bas], bd * 0.8, 0.65, 'sine', time)
      this.note(this.N[bas] * 2, bd * 0.5, 0.18, 'triangle', time)
    }

    // Chord stab every 4 beats
    if (idx % 4 === 0) {
      const chord = this.chords[(idx / 4) % this.chords.length]
      chord.forEach(n => {
        if (this.N[n]) this.note(this.N[n], bd * 0.28, 0.1, 'sine', time)
      })
    }

    // Hi-hat
    this.noise(0.04, idx % 4 === 0 ? 0.16 : 0.07, time, true)

    // Kick on beats 0 and 8
    if (idx % 16 === 0 || idx % 16 === 8) {
      this.note(160, bd * 0.3, 0.85, 'sine', time)
      this.note(55,  bd * 0.4, 0.55, 'sine', time + 0.01)
    }

    // Snare on beats 4 and 12
    if (idx % 16 === 4 || idx % 16 === 12) {
      this.note(200, bd * 0.15, 0.28, 'triangle', time)
      this.noise(0.14, 0.22, time)
    }
  }

  private schedule() {
    if (!actx || !this.running) return
    while (this.nextBeat < actx.currentTime + this.lookahead) {
      this.playBeat(this.beatNum, this.nextBeat)
      this.nextBeat += this.beatDur()
      this.beatNum++
    }
    this.scheduleId = setTimeout(() => this.schedule(), 50)
  }

  start() {
    if (!actx || this.running) return
    resume()
    this.running  = true
    this.beatNum  = 0
    this.gainNode = actx.createGain()
    this.gainNode.gain.setValueAtTime(0, actx.currentTime)
    this.gainNode.gain.linearRampToValueAtTime(this.vol, actx.currentTime + 1.5)
    this.gainNode.connect(actx.destination)
    this.nextBeat = actx.currentTime + 0.1
    this.schedule()
  }

  stop() {
    this.running = false
    if (this.scheduleId) clearTimeout(this.scheduleId)
    if (this.gainNode && actx) {
      this.gainNode.gain.linearRampToValueAtTime(0, actx.currentTime + 1)
      const gn = this.gainNode
      setTimeout(() => { gn.disconnect() }, 1200)
      this.gainNode = null
    }
  }

  setVolume(v: number) {
    this.vol = v
    if (this.gainNode && actx) {
      this.gainNode.gain.setValueAtTime(v, actx.currentTime)
    }
  }

  isPlaying() { return this.running }
}

export const Music = new BackgroundMusic()
